/**
 * `server.cfg`: reading one, writing one, and merging into one without
 * destroying what the owner wrote.
 *
 * A `resources/` folder does not start itself. Load order in FiveM is whatever
 * `server.cfg` says it is, so a folder where every resource is individually
 * perfect still produces a server that will not boot if `es_extended` is
 * `ensure`d before `oxmysql`. That is the fault this module exists for, and it
 * is invisible from inside any single resource — nothing in `ox_inventory` can
 * tell you that `ox_lib` starts after it.
 *
 * The competitor's generator is sold on exactly this: its registry line is "A
 * working server.cfg, in the right order" and its description says
 * "correctly-ordered". It contains no ordering logic at all — the only thing
 * that moves a resource is a manual adjacent-swap button — so the headline
 * promise of the tool is the one thing it does not do.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THIS MODULE IS A SURFACE, NOT A SECOND ORDERING
 *
 * `dependencies.ts` already computes the order: `buildDependencyGraph` resolves
 * who needs whom (including the implicit needs nobody declares — `oxmysql`,
 * `PolyZone`, `mysql-async` — through `inferDependencies`), `findCycles` names
 * the loops, and `loadOrder` sorts what is left, collapsing each loop rather
 * than dropping its members. All of that is tested there.
 *
 * So nothing here sorts anything. `orderCfgResources` reads `graph.order` and
 * `graph.cycles` and maps node ids back onto the folders they came from. If this
 * file ever grows a topological sort, that is the bug: two orderings in one
 * repo is how the tool that generates a config and the tool that checks one
 * start disagreeing about the same folder.
 *
 * `ensureBlock` in that module is deliberately *not* called here, and the
 * difference is worth stating. `ensureBlock` writes a self-contained block with
 * its own comments and its own cap, for a "copy these lines into your cfg"
 * button. This file is writing or editing a whole config: it needs the ordered
 * names, not a block of text, and it has to keep each name's bracket category
 * alongside it. `serverCfg.test.ts` asserts the two agree on order for the same
 * graph, which is what keeps "a surface over it" true rather than aspirational.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE FIVE THINGS THIS GETS RIGHT THAT THE PRIOR ART DOES NOT
 *
 *   1. Order. See above.
 *
 *   2. Quoting. Theirs escapes the double quote and nothing else, so a hostname
 *      ending in a backslash writes `"My Server \"` — the backslash escapes its
 *      own closing quote and the rest of the file is inside a string. A `#`
 *      outside quotes starts a comment and a newline splits one directive into
 *      two. `quoteCfgValue` escapes the backslash first and the quote second,
 *      and always quotes, which is what makes `#` and `;` inert. A newline
 *      cannot be escaped at all — `server.cfg` has no line-continuation — so it
 *      is replaced with a space and *reported*, never silently swallowed.
 *
 *   3. Clamping says so. Theirs rewrites 999999 max clients to 2048 with no
 *      feedback: the input box and the generated file disagree and nothing on
 *      screen reconciles them. Every clamp here comes back as a `CfgAdjustment`
 *      with the value given, the value used and the reason, the generated file
 *      carries those as comments at the top, and the surface renders them. A
 *      clamp that is not reported is this layer's honesty rule being broken.
 *
 *   4. Cycles are named, not dropped. `dependencies.ts` does that work; this
 *      module turns it into `cfg.ensure.cycle` naming every member, and the
 *      generated order still starts all of them.
 *
 *   5. `sv_enforceGameBuild` is a choice. Theirs hard-codes `2802` — Los Santos
 *      Drug Wars, December 2022 — into every file it writes, with no control
 *      anywhere, so a pack streaming anything newer loads nothing. Here it is a
 *      value with a caption from `data/gameBuilds.v1.ts`, and a build several
 *      releases behind the latest produces `cfg.build.stale`.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * SCOPE — what this deliberately does not do
 *
 * `ParsedCfg.order` is the start order of *this file*. A cfg that `exec`s
 * another file has an order this module cannot see, and it says so rather than
 * following the `exec` — reading it would mean a file system this module does
 * not have. Anything downstream that reasons about "which copy of a colliding
 * asset wins today" has to carry that caveat with it.
 *
 * Nothing here validates a value against a running server. `sv_licenseKey`
 * being non-empty is not the same as it being valid, a port being in range is
 * not the same as it being free, and this module never claims otherwise.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHERE THIS DIVERGES FROM THE SHAPE THE BRIEF WROTE DOWN, AND WHY
 *
 * The brief specified `CfgLine`, `ParsedCfg`, `parseServerCfg`,
 * `generateServerCfg` and `mergeServerCfg`. Those are here as written apart from
 * four deliberate changes, each of which exists to keep a rule this layer
 * already has:
 *
 *   `raw` on every `CfgLine` variant, not three of five. The merge rewrites
 *   whole lines, and `GtaTextEdit` refuses to apply unless it is told the text
 *   that currently occupies the span. Without the raw line there is nothing
 *   honest to put in `expect` — only a reconstructed guess at how the user
 *   spelled their own line, which is precisely the check `expect` exists to make
 *   impossible to skip.
 *
 *   `truncated` on `ParsedCfg`. The parse has caps, and a cap with no channel to
 *   report itself is a silent lie about how much of the file was read.
 *
 *   An optional `options` on `parseServerCfg`, carrying the file name onto every
 *   span, because a `GtaTextEdit` needs a file and inventing `"server.cfg"` at
 *   the point of the edit would write a patch addressed to a file that might not
 *   be the one that was read.
 *
 *   `analyzeServerCfg`, which the brief does not name. Something has to emit the
 *   eleven `cfg.*` codes — and `dependency.missing`, which it takes from
 *   `dependencyFindings` rather than restating, because "missing dependencies
 *   reported rather than silently dropped" is half of the brief's sentence for
 *   folder mode and `dependencies.ts` already answers it. It emits none of that
 *   module's other codes: its cycle is `cfg.ensure.cycle` here, and its inferred
 *   edge is the `inferred` tag on `cfg.ensure.order`. Nine of the eleven are
 *   about a folder or an existing file rather than about the config being
 *   written, and the two that are not (`cfg.license.placeholder`,
 *   `cfg.build.stale`) still have to fire in import mode where nothing is being
 *   generated at all. It returns `skipped` alongside `findings` for the same
 *   reason `GtaResourceReport` does.
 *
 * The surrounding exports — `normalizeCfgConfig`, `orderCfgResources`,
 * `quoteCfgValue`, `sanitizeCfgValue`, `readCfgValue`, `cfgResourcesFromRoots`
 * — are public because each is a rule two callers would otherwise write twice:
 * the generator and the merge, or this module and the page above it.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import {
  GAME_BUILDS_STAMP,
  gameBuild,
  gameBuildLabel,
  gameBuildsBehind,
  latestGameBuild,
} from "./data/gameBuilds.v1";
import { frameworkFamily } from "./data/frameworkResources.v1";
import { dependencyFindings } from "./dependencies";
import { isServerProvidedResource } from "./data/serverProvidedResources.v1";
import { normalizeName } from "./normalize";
import type {
  DependencyGraph,
  GtaFinding,
  GtaSeverity,
  GtaSourceSpan,
  GtaTextEdit,
} from "./types";

// ─── Caps ────────────────────────────────────────────────────────────────────

/**
 * Every cap here has a number and a sentence, and the sentence reaches the user.
 * A `server.cfg` is a small file; these exist because the text arriving here was
 * pasted by a stranger and might be a 40 MB log with the wrong extension.
 */

/** Characters of config text read. Beyond this the tail is not parsed. */
const MAX_CFG_BYTES = 2 * 1024 * 1024;

/** Lines parsed. A real `server.cfg` is tens of lines; this is a runaway guard. */
const MAX_CFG_LINES = 20_000;

/** Findings returned, so one pathological file cannot flood the panel. */
const MAX_CFG_FINDINGS = 500;

/** `ensure` lines written into a generated file. */
const MAX_ENSURE_LINES = 2000;

/** Admin identifiers written, and tags kept. */
const MAX_ADMINS = 200;
const MAX_TAGS = 32;

/**
 * The highest `sv_maxclients` the Cfx artifacts accept.
 *
 * This number has moved — it was 32, then 64, then 1024, now 2048 — and it is
 * the artifact's number, not ours. It is the ceiling a value is clamped to, and
 * the clamp is always reported, so a user running artifacts with a different cap
 * sees what we did and can override it.
 */
const MAX_CLIENTS_CEILING = 2048;

/** Below this, `sv_maxclients` is not a server. */
const MIN_CLIENTS = 1;

/**
 * What OneSync off actually allows.
 *
 * The server silently caps at 32 slots with OneSync disabled. We do *not* clamp
 * to it — rewriting the owner's 64 down to 32 would be the competitor's silent
 * rewrite in a different costume — we write what they asked and raise
 * `cfg.onesync.maxclients` so they know the server will ignore it.
 */
const ONESYNC_OFF_CLIENT_CAP = 32;

/** The default FiveM port, used when none is given. */
const DEFAULT_PORT = 30120;

/** The FiveM default slot count. */
const DEFAULT_MAX_CLIENTS = 48;

/**
 * How many releases behind the latest build counts as stale.
 *
 * Three, because one or two behind is an ordinary server that has not restarted
 * since the last DLC and a warning there is noise. Three is roughly eighteen
 * months of content a resource can stream and this server cannot load.
 */
const STALE_BUILDS_BEHIND = 3;

// ─── The parsed shape ────────────────────────────────────────────────────────

/**
 * One line of a `server.cfg`, classified, with the text it was written as.
 *
 * Comments and blanks are lines like any other. Keeping them is what lets the
 * merge edit around them instead of regenerating the file: a config that has
 * been maintained for two years has comments in it that are worth more than
 * anything this tool can write, and a "merge" that loses them is a rewrite.
 */
export type CfgLine =
  | { kind: "ensure"; resource: string; raw: string; span: GtaSourceSpan }
  | {
      kind: "set";
      name: string;
      value: string;
      /**
       * True for `setr` and `sets` — the forms whose value leaves the server.
       *
       * The two are folded into one boolean deliberately: every consumer here
       * asks "does this leave the server", never "which keyword was it", and the
       * keyword itself is still in `raw` for anything that needs to know.
       */
      replicated: boolean;
      raw: string;
      span: GtaSourceSpan;
    }
  | { kind: "exec"; path: string; raw: string; span: GtaSourceSpan }
  | { kind: "command"; raw: string; span: GtaSourceSpan }
  | { kind: "comment" | "blank"; raw: string; span: GtaSourceSpan };

export type ParsedCfg = {
  lines: CfgLine[];
  /**
   * Start order, in file order — what actually decides which asset wins.
   *
   * Names exactly as written, duplicates kept. Duplicates are kept because a
   * duplicate is itself a finding, and because de-duplicating here would move
   * every later index and quietly change the answer to "which one wins".
   *
   * This is the order of *this file only*. An `exec` line points at a config
   * this module never reads, so a cfg that execs its resource list has an order
   * that is not here. `ParsedCfg.lines` carries the `exec` lines so a caller can
   * say so.
   */
  order: string[];
  /**
   * Bracket categories this file starts as a unit, lower-cased, `[cars]`.
   *
   * A category is not a resource: `ensure [cars]` starts everything inside the
   * `[cars]` folder, and `ensure [cars]` on a folder that is not a bracket
   * folder does nothing at all and reports no error anywhere.
   */
  categories: Set<string>;
  /** What was not read, in words a user can act on. Empty when nothing was. */
  truncated: string[];
};

/**
 * A resource the config might start, and the bracket folder it sits in.
 *
 * `category` is the enclosing `[cars]` folder when there is one, because that is
 * the difference between `ensure police` and `ensure [cars]` covering it. It is
 * not part of the name: FiveM's resource name is the leaf folder, and a config
 * that wrote `ensure [cars]/police` would be wrong.
 */
export type CfgResource = {
  /** The folder name, exactly as it is on disk, spaces and all. */
  name: string;
  /** `[cars]`, when the resource lives inside one. */
  category?: string;
};

// ─── Parsing ─────────────────────────────────────────────────────────────────

/**
 * Where a comment starts on a line, or −1.
 *
 * Quote-aware, and that is the whole point: `sv_hostname "Bob's #1 Server"`
 * contains a `#` that is part of the hostname, and a parser that cuts at the
 * first `#` reads the hostname as `Bob's ` and loses the rest of the file's
 * meaning one line at a time. A backslash inside quotes escapes the next
 * character, so `"a \" b # c"` is one string with a `#` in it.
 *
 * Only `#` is treated as a comment. `//` is not a `server.cfg` comment, and
 * treating it as one would cut an unquoted URL in half; `;` is a command
 * separator in the Cfx console rather than a comment, which is why every value
 * this module *writes* is quoted — a `;` inside quotes cannot split a command.
 */
function commentStart(line: string): number {
  let quoted = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (quoted) {
      if (ch === "\\") {
        i += 1;
        continue;
      }
      if (ch === '"') quoted = false;
      continue;
    }
    if (ch === '"') {
      quoted = true;
      continue;
    }
    if (ch === "#") return i;
  }
  return -1;
}

/** The code half of a line and the comment half, either of which may be empty. */
function splitComment(line: string): { code: string; comment: string } {
  const at = commentStart(line);
  return at === -1
    ? { code: line, comment: "" }
    : { code: line.slice(0, at), comment: line.slice(at) };
}

/**
 * A command line split into its arguments, the way the console tokenizer does.
 *
 * Quoted runs are one token with their escapes resolved, so
 * `ensure "my resource"` is one resource and `set a "b c"` is one value. An
 * unterminated quote takes the rest of the line rather than throwing, because a
 * half-typed line in the middle of a file must not stop the other ninety from
 * parsing.
 */
function tokenize(code: string): string[] {
  const tokens: string[] = [];
  let current = "";
  let started = false;
  let quoted = false;

  for (let i = 0; i < code.length; i += 1) {
    const ch = code[i];
    if (quoted) {
      if (ch === "\\" && i + 1 < code.length) {
        current += code[i + 1];
        i += 1;
        continue;
      }
      if (ch === '"') {
        quoted = false;
        continue;
      }
      current += ch;
      continue;
    }
    if (ch === '"') {
      quoted = true;
      started = true;
      continue;
    }
    if (ch === " " || ch === "\t") {
      if (started) {
        tokens.push(current);
        current = "";
        started = false;
      }
      continue;
    }
    current += ch;
    started = true;
  }
  if (started) tokens.push(current);
  return tokens;
}

/** `[cars]` — a bracket category rather than a resource. */
function isCategoryName(name: string): boolean {
  return name.length > 2 && name.startsWith("[") && name.endsWith("]");
}

/** The three keywords that start a resource. `restart` is not one: it re-starts. */
const START_KEYWORDS: ReadonlySet<string> = new Set(["ensure", "start"]);

/** `set`, `setr`, `sets`, and which of them leave the server. */
const SET_KEYWORDS: ReadonlyMap<string, boolean> = new Map([
  ["set", false],
  ["setr", true],
  ["sets", true],
]);

/**
 * Read a `server.cfg` into lines, a start order and its categories.
 *
 * Every line comes back, including the ones this module has no opinion about,
 * and every line carries the offsets a patch needs. That is not tidiness: the
 * merge writes `GtaTextEdit`s against these offsets, and [Clash Finder] reads
 * `order` to say which copy of a colliding asset is the one the server is
 * actually running today.
 */
export function parseServerCfg(text: string, options?: { file?: string }): ParsedCfg {
  const file = typeof options?.file === "string" ? options.file : undefined;
  const truncated: string[] = [];

  if (typeof text !== "string") {
    return { lines: [], order: [], categories: new Set(), truncated };
  }

  let source = text;
  if (source.length > MAX_CFG_BYTES) {
    source = source.slice(0, MAX_CFG_BYTES);
    truncated.push(
      `This config is ${text.length.toLocaleString()} characters and only the first ` +
        `${MAX_CFG_BYTES.toLocaleString()} were read. Anything after that point — including ` +
        `any ensure lines — has not been checked.`,
    );
  }

  // Line boundaries by hand rather than `split`, because every line needs the
  // offset it starts at and `split` throws those away.
  const rawLines: { raw: string; start: number }[] = [];
  let lineStart = 0;
  for (let i = 0; i <= source.length; i += 1) {
    if (i !== source.length && source.charCodeAt(i) !== 10) continue;
    let end = i;
    if (end > lineStart && source.charCodeAt(end - 1) === 13) end -= 1;
    rawLines.push({ raw: source.slice(lineStart, end), start: lineStart });
    lineStart = i + 1;
    if (i === source.length) break;
  }

  let kept = rawLines;
  if (kept.length > MAX_CFG_LINES) {
    kept = kept.slice(0, MAX_CFG_LINES);
    truncated.push(
      `This config has ${rawLines.length.toLocaleString()} lines and only the first ` +
        `${MAX_CFG_LINES.toLocaleString()} were parsed.`,
    );
  }

  const lines: CfgLine[] = [];
  const order: string[] = [];
  const categories = new Set<string>();

  kept.forEach((entry, index) => {
    const span: GtaSourceSpan = {
      line: index + 1,
      column: 1,
      byteStart: entry.start,
      byteEnd: entry.start + entry.raw.length,
    };
    if (file !== undefined) span.file = file;

    const { code } = splitComment(entry.raw);
    if (entry.raw.trim() === "") {
      lines.push({ kind: "blank", raw: entry.raw, span });
      return;
    }
    if (code.trim() === "") {
      lines.push({ kind: "comment", raw: entry.raw, span });
      return;
    }

    const tokens = tokenize(code);
    const keyword = (tokens[0] ?? "").toLowerCase();

    if (START_KEYWORDS.has(keyword) && tokens.length >= 2) {
      const resource = tokens[1];
      lines.push({ kind: "ensure", resource, raw: entry.raw, span });
      order.push(resource);
      if (isCategoryName(resource)) categories.add(resource.toLowerCase());
      return;
    }

    const replicated = SET_KEYWORDS.get(keyword);
    if (replicated !== undefined && tokens.length >= 2) {
      lines.push({
        kind: "set",
        name: tokens[1],
        value: tokens[2] ?? "",
        replicated,
        raw: entry.raw,
        span,
      });
      return;
    }

    if (keyword === "exec" && tokens.length >= 2) {
      lines.push({ kind: "exec", path: tokens[1], raw: entry.raw, span });
      return;
    }

    lines.push({ kind: "command", raw: entry.raw, span });
  });

  return { lines, order, categories, truncated };
}

/**
 * The value a config gives a convar, or `null`.
 *
 * Later wins, because that is what the server does: a cfg that says
 * `sv_maxclients 32` and then `sv_maxclients 64` runs 64, and a reader that took
 * the first would report on a value the server never used.
 *
 * Both spellings count. `sv_hostname "x"` is a bare command and
 * `set sv_hostname "x"` is a convar assignment, and the same setting is written
 * both ways across the ecosystem's example configs.
 */
export function readCfgValue(cfg: ParsedCfg | null | undefined, name: string): string | null {
  if (!cfg || !Array.isArray(cfg.lines)) return null;
  const wanted = name.trim().toLowerCase();
  let found: string | null = null;
  for (const line of cfg.lines) {
    if (line.kind === "set") {
      if (line.name.toLowerCase() === wanted) found = line.value;
      continue;
    }
    if (line.kind !== "command") continue;
    const tokens = tokenize(splitComment(line.raw).code);
    if ((tokens[0] ?? "").toLowerCase() === wanted) found = tokens[1] ?? "";
  }
  return found;
}

// ─── Quoting ─────────────────────────────────────────────────────────────────

/**
 * Characters a `server.cfg` value cannot hold, whatever we do to them.
 *
 * A newline is the important one. There is no line continuation in this format,
 * so a hostname with a line break in it cannot be written at all — theirs writes
 * it anyway and splits one directive into two lines, the second of which the
 * server tries to run as a command. Escaping is not available either: the
 * console tokenizer resolves `\\` and `\"` and does not decode `\n`, so writing
 * `\n` would put a literal backslash-n into the server browser.
 *
 * So they are replaced with a space, and the replacement is reported. Losing a
 * character loudly is the only honest option when the format cannot carry it.
 */
/**
 * Everything unwritable — and note what is *not* in the range: a tab.
 *
 * A tab inside a quoted value survives the tokenizer intact and shows up as
 * whitespace, so there is nothing to fix and nothing to report. Replacing it
 * would be a silent change to a value that was already fine, which is the habit
 * this module exists to break. This range and the two tests in
 * `sanitizeCfgValue` cover exactly the same characters on purpose: a character
 * replaced with no note attached is a lie by omission.
 */
// Matching control characters is the entire job of this sanitiser. `no-control-regex`
// exists to catch one typed into a pattern by accident; every codepoint in this
// range is deliberate and argued for directly above.
// eslint-disable-next-line no-control-regex
const CONTROL_CHARACTERS = /[\u0000-\u0008\u000a-\u001f\u007f]/g;

export type CfgSanitized = {
  value: string;
  /** One sentence per thing that had to change. Empty when nothing did. */
  notes: string[];
};

/**
 * Strip what the format cannot carry, and say what was stripped.
 *
 * Separate from `quoteCfgValue` because the two have different audiences: this
 * one produces sentences for a person, that one produces bytes for a file. A
 * caller that only needs bytes gets sanitising anyway — `quoteCfgValue` calls
 * this — so there is no path that writes a raw control character.
 */
export function sanitizeCfgValue(raw: string): CfgSanitized {
  const value = typeof raw === "string" ? raw : "";
  const notes: string[] = [];

  let cleaned = value;
  if (/[\r\n]/.test(cleaned)) {
    notes.push(
      "A line break was replaced with a space. `server.cfg` has no way to write one inside a " +
        "value: left in, it would end the directive early and the server would try to run the " +
        "rest of your text as a command.",
    );
  }
  // The same deliberate range as `CONTROL_CHARACTERS`, minus the two codepoints
  // the line-break test above already owns.
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/.test(cleaned)) {
    notes.push(
      "A control character was replaced with a space. It would not have shown up in the server " +
        "browser and some of them stop the config parser outright.",
    );
  }
  if (notes.length > 0) {
    // A run of replacements collapses, so a pasted paragraph does not become
    // forty spaces in the middle of a hostname. Only when something was actually
    // replaced: collapsing a double space the user typed on purpose would be a
    // change to their value that nothing told them about.
    cleaned = cleaned.replace(CONTROL_CHARACTERS, " ").replace(/ {2,}/g, " ");
  }
  // Trimmed always, and silently, because leading and trailing whitespace inside
  // a quoted value is invisible everywhere it is shown — the server browser, the
  // console, this tool — so reporting it would be reporting a change nobody can
  // see to a value nobody typed deliberately.
  return { value: cleaned.trim(), notes };
}

/**
 * A value, quoted so that a `server.cfg` reads it back unchanged.
 *
 * The order of the two escapes is the bug in every naive version of this. The
 * backslash has to be doubled *first*: escape the quote first and the backslash
 * you just wrote gets doubled by the second pass, and `My Server "X"` becomes
 * `My Server \\"X\\"`. Doubling backslashes first is also what fixes the
 * trailing-backslash case — `Bob\` writes as `"Bob\\"` instead of `"Bob\"`,
 * which would have escaped its own closing quote and swallowed the rest of the
 * file.
 *
 * Always quoted, even for a value that needs none. That is what makes `#` and
 * `;` inert without a second rule, and it means a value that later grows a space
 * does not need this decision revisited.
 */
export function quoteCfgValue(raw: string): string {
  const { value } = sanitizeCfgValue(raw);
  return `"${value.replace(/\\/g, "\\\\").replace(/"/g, '\\"')}"`;
}

// ─── The configuration a form or a folder produces ───────────────────────────

/** `off`, `legacy` or `on` — the three values `set onesync` takes. */
export type OneSyncMode = "off" | "legacy" | "on";

/** A convar the user added that this module has no opinion about. */
export type CfgExtraSet = {
  name: string;
  value: string;
  /** `set` by default; `setr` reaches clients and `sets` reaches the browser. */
  scope?: "set" | "setr" | "sets";
};

/**
 * Everything a generated config is built from.
 *
 * Every field is optional, because the three modes fill different halves of it:
 * the form mode has a hostname and no resources, the folder mode has resources
 * and no licence key, and the import mode has whatever the existing file held.
 * A field left undefined is not written at all — which is different from a field
 * set to the empty string, which is written as a commented-out placeholder so
 * the user can see the shape of the line they are missing.
 */
export type CfgConfig = {
  hostname?: string;
  /**
   * `sv_projectName` and `sv_projectDesc` — what the FiveM server browser shows.
   *
   * Named here because the competitor's generator consumes both and exposes
   * neither: they are dead fields in its UI, so every config it writes carries
   * whatever its defaults were and the owner has no way to change the two
   * strings most people actually read.
   */
  projectName?: string;
  projectDesc?: string;
  locale?: string;
  tags?: readonly string[];
  port?: number;
  maxClients?: number;
  licenseKey?: string;
  steamWebApiKey?: string;
  rconPassword?: string;
  oneSync?: OneSyncMode;
  oneSyncPopulation?: boolean;
  /** A `sv_enforceGameBuild` value, or `null` to leave the server on its default. */
  enforceGameBuild?: string | number | null;
  scriptHookAllowed?: boolean;
  endpointPrivacy?: boolean;
  mysqlConnectionString?: string;
  /** Identifiers to put in `group.admin`, `license:abc…` or `fivem:1`. */
  admins?: readonly string[];
  /** The folder's resources, in the order they should start. */
  resources?: readonly CfgResource[];
  /** `exec` lines to keep, in order. */
  execs?: readonly string[];
  extraSets?: readonly CfgExtraSet[];
  /**
   * Whether to start the resources the server itself ships.
   *
   * Defaults to true, because a config without them is a server with no chat and
   * no spawn manager. The names come from `data/serverProvidedResources.v1.ts`
   * so there is one list of what the server provides, not two.
   */
  startServerProvided?: boolean;
};

/**
 * Something the generator did to a value the user gave it.
 *
 * This type is the fix for the competitor's silent clamp, and it is deliberately
 * not a finding code: the eleven `cfg.*` codes are fixed and permanent, and an
 * adjustment is not a fault in the user's config — it is a fact about what this
 * tool wrote. It travels as data so the surface can render it, and the generated
 * file repeats it as a comment so the file is self-explaining when it is read a
 * year later with no tool in front of it.
 */
export type CfgAdjustment = {
  /** The directive it is about: `sv_maxclients`. */
  field: string;
  /** What the user gave, as text. */
  given: string;
  /** What went into the file. */
  used: string;
  /** Why, in a full sentence. */
  reason: string;
};

export type NormalizedCfg = { config: CfgConfig; adjustments: CfgAdjustment[] };

function clampInteger(
  value: number,
  min: number,
  max: number,
): { value: number; changed: "rounded" | "raised" | "lowered" | null } {
  if (!Number.isFinite(value)) return { value: min, changed: "raised" };
  const rounded = Math.round(value);
  if (rounded < min) return { value: min, changed: "raised" };
  if (rounded > max) return { value: max, changed: "lowered" };
  return { value: rounded, changed: rounded === value ? null : "rounded" };
}

/**
 * Bring a configuration inside what the format allows, and list every change.
 *
 * Both `generateServerCfg` and `analyzeServerCfg` run through this, so what the
 * file says and what the panel says cannot disagree — which is the actual defect
 * behind "the input box says 999999 and the file says 2048".
 */
export function normalizeCfgConfig(config: CfgConfig): NormalizedCfg {
  const source = config && typeof config === "object" ? config : {};
  const adjustments: CfgAdjustment[] = [];
  const next: CfgConfig = { ...source };

  const text = (field: string, key: keyof CfgConfig): void => {
    const raw = source[key];
    if (typeof raw !== "string") return;
    const { value, notes } = sanitizeCfgValue(raw);
    (next as Record<string, unknown>)[key] = value;
    for (const note of notes) {
      adjustments.push({ field, given: raw, used: value, reason: note });
    }
  };

  text("sv_hostname", "hostname");
  text("sv_projectName", "projectName");
  text("sv_projectDesc", "projectDesc");
  text("sets locale", "locale");
  text("sv_licenseKey", "licenseKey");
  text("steam_webApiKey", "steamWebApiKey");
  text("rcon_password", "rconPassword");
  text("mysql_connection_string", "mysqlConnectionString");

  if (typeof source.port === "number") {
    const { value, changed } = clampInteger(source.port, 1, 65_535);
    next.port = value;
    if (changed !== null) {
      adjustments.push({
        field: "endpoint_add_tcp / endpoint_add_udp",
        given: String(source.port),
        used: String(value),
        reason:
          `A port has to be a whole number between 1 and 65535, so this file uses ${value}. ` +
          "Change it if that is not the port your host gave you.",
      });
    }
  }

  if (typeof source.maxClients === "number") {
    const { value, changed } = clampInteger(
      source.maxClients,
      MIN_CLIENTS,
      MAX_CLIENTS_CEILING,
    );
    next.maxClients = value;
    if (changed !== null) {
      adjustments.push({
        field: "sv_maxclients",
        given: String(source.maxClients),
        used: String(value),
        reason:
          `The server artifacts accept between ${MIN_CLIENTS} and ${MAX_CLIENTS_CEILING} slots, ` +
          `so this file says ${value} rather than ${source.maxClients}. You asked for something ` +
          "outside that range and this is the number your server will actually run.",
      });
    }
  }

  if (Array.isArray(source.tags)) {
    const tags: string[] = [];
    for (const tag of source.tags) {
      if (typeof tag !== "string") continue;
      const { value } = sanitizeCfgValue(tag);
      const trimmed = value.replace(/,/g, " ").trim();
      if (trimmed !== "" && tags.length < MAX_TAGS) tags.push(trimmed);
    }
    next.tags = tags;
    if (Array.isArray(source.tags) && source.tags.length > tags.length) {
      adjustments.push({
        field: "sets tags",
        given: `${source.tags.length} tags`,
        used: `${tags.length} tags`,
        reason:
          `Blank tags were dropped, commas inside a tag were turned into spaces (the tag list is ` +
          `comma-separated, so a comma inside one splits it), and at most ${MAX_TAGS} are written.`,
      });
    }
  }

  if (Array.isArray(source.admins)) {
    const admins = source.admins
      .filter((id): id is string => typeof id === "string")
      .map((id) => sanitizeCfgValue(id).value)
      .filter((id) => id !== "")
      .slice(0, MAX_ADMINS);
    next.admins = admins;
    if (source.admins.length > admins.length) {
      adjustments.push({
        field: "add_principal",
        given: `${source.admins.length} identifiers`,
        used: `${admins.length} identifiers`,
        reason: `Blank identifiers were dropped and at most ${MAX_ADMINS} are written.`,
      });
    }
  }

  return { config: next, adjustments };
}

// ─── Ordering: a surface over `dependencies.ts` ───────────────────────────────

export type OrderedCfgResources = {
  resources: CfgResource[];
  /** Where the order came from, so a caller never implies one it did not compute. */
  source: "dependency-graph" | "as-given";
  /** Sentences about what the order could not resolve. Empty when it resolved. */
  notes: string[];
};

/**
 * Put a folder's resources into the order they should start in.
 *
 * This reads `graph.order` — which `buildDependencyGraph` filled from
 * `loadOrder` — and `graph.cycles`, and maps node ids back onto the folders they
 * came from. It sorts nothing itself. See the header: one ordering per repo.
 *
 * With no graph, or with a graph too tangled for `loadOrder` to answer, the
 * resources come back in the order they were given and `source` says `as-given`.
 * That is the honest answer and it is why `source` exists: a caller that printed
 * "correctly ordered" over an as-given list would be making the competitor's
 * exact claim.
 */
export function orderCfgResources(
  resources: readonly CfgResource[],
  graph?: DependencyGraph | null,
): OrderedCfgResources {
  const list = Array.isArray(resources)
    ? resources.filter(
        (item): item is CfgResource =>
          item !== null && typeof item === "object" && typeof item.name === "string",
      )
    : [];
  const notes: string[] = [];

  const order = graph && Array.isArray(graph.order) ? graph.order : null;
  if (!order) {
    if (graph) {
      notes.push(
        "The dependency graph was too tangled to sort, so these resources are in the order they " +
          "were given and have not been ordered. Check them by hand before starting the server.",
      );
    } else {
      notes.push(
        "No dependency graph was supplied, so these resources are in the order they were given " +
          "and have not been ordered.",
      );
    }
    return { resources: [...list], source: "as-given", notes };
  }

  const byId = new Map<string, CfgResource>();
  for (const item of list) {
    const id = normalizeName(item.name);
    if (id !== "" && !byId.has(id)) byId.set(id, item);
  }

  const cycles = Array.isArray(graph?.cycles) ? graph.cycles : [];
  const cycleByRepresentative = new Map(
    cycles.filter((cycle) => Array.isArray(cycle) && cycle.length > 0).map((c) => [c[0], c]),
  );

  const out: CfgResource[] = [];
  const placed = new Set<string>();
  for (const id of order) {
    for (const member of cycleByRepresentative.get(id) ?? [id]) {
      const resource = byId.get(member);
      if (!resource || placed.has(member)) continue;
      placed.add(member);
      out.push(resource);
    }
  }

  // Anything the graph never saw keeps its given position, at the end. A folder
  // with no manifest is still a folder that has to start.
  const strays: string[] = [];
  for (const item of list) {
    const id = normalizeName(item.name);
    if (id === "" || placed.has(id)) continue;
    placed.add(id);
    out.push(item);
    strays.push(item.name);
  }
  if (strays.length > 0) {
    notes.push(
      `${strays.length === 1 ? "One resource was" : `${strays.length} resources were`} not in the ` +
        `dependency graph (${strays.slice(0, 5).join(", ")}${strays.length > 5 ? ", …" : ""}) and ` +
        "kept its place at the end of the list. Nothing is known about what it needs.",
    );
  }

  if (cycles.length > 0) {
    notes.push(
      `${cycles.length === 1 ? "One dependency cycle was" : `${cycles.length} dependency cycles were`} ` +
        "collapsed to keep an order on offer. Every member still starts, but no order inside a " +
        "cycle is correct — see the cycle findings for the names.",
    );
  }

  return { resources: out, source: "dependency-graph", notes };
}

/**
 * Turn drop-relative resource roots into the shape this module wants.
 *
 * `intake.ts` hands back a `root` per resource — `vendor-b.zip/[cars]/police` —
 * and the two facts a config needs out of that string are the leaf folder name
 * and whether a bracket category encloses it. Written once here so the page
 * stage does not invent a second bracket rule: getting it wrong means either
 * `ensure [cars]/police`, which is not a resource name, or losing the category
 * and never being able to explain an `ensure [cars]` line the user already has.
 */
export function cfgResourcesFromRoots(roots: readonly string[]): CfgResource[] {
  if (!Array.isArray(roots)) return [];
  const out: CfgResource[] = [];
  for (const root of roots) {
    if (typeof root !== "string") continue;
    const segments = root.split("/").filter((segment) => segment !== "");
    const name = segments[segments.length - 1];
    if (name === undefined || name === "" || isCategoryName(name)) continue;
    const parent = segments[segments.length - 2];
    out.push(
      parent !== undefined && isCategoryName(parent) ? { name, category: parent } : { name },
    );
  }
  return out;
}

// ─── Directives: one renderer, shared by generate and merge ──────────────────

type Directive = {
  /** How an existing line is recognised as the same setting. */
  key: string;
  /** The line to write. */
  line: string;
  /**
   * True for a line written commented out.
   *
   * Worth keeping from the competitor: `# set steam_webApiKey "none"` teaches
   * the shape of the line and gives the reader something to uncomment, which is
   * more useful than an absence. The merge never writes one of these into an
   * existing file, because a commented suggestion in somebody's maintained
   * config is clutter, not help.
   */
  commented: boolean;
  /**
   * The setting and the value, apart from the spelling of the line.
   *
   * A merge rewrites an existing line in that line's own keyword, and this is
   * what it needs to do it: the name, the rendered value, and the scope this
   * module would have chosen if it were writing the line from scratch. Absent on
   * the commented placeholders, which a merge never writes into a maintained
   * file at all.
   */
  convar?: { name: string; value: string; scope: "cmd" | "set" | "setr" | "sets" };
};

/**
 * `sv_hostname "x"` and `set sv_hostname "x"`, as one comparable key.
 *
 * One key per convar name, whatever keyword the line is written with, because
 * they are one setting: `readCfgValue` says so in its own docstring — "the same
 * setting is written both ways across the ecosystem's example configs" — and a
 * writer that disagreed with the reader in the same module produced a merge that
 * appended a second `sv_hostname` line under the user's `set sv_hostname` one,
 * leaving the file asserting two different names and the panel reporting
 * "1 line changed" over nothing changed in place.
 *
 * Bare commands that are not convars (`add_ace`, `add_principal`) key on their
 * first token too, which is harmless: `directivesFor` emits none of them, so
 * nothing ever matches those keys.
 */
function directiveKey(line: CfgLine): string | null {
  if (line.kind === "set") return `var:${line.name.toLowerCase()}`;
  if (line.kind !== "command") return null;
  const tokens = tokenize(splitComment(line.raw).code);
  const keyword = (tokens[0] ?? "").toLowerCase();
  return keyword === "" ? null : `var:${keyword}`;
}

function command(name: string, value: string): Directive {
  return {
    key: `var:${name.toLowerCase()}`,
    line: `${name} ${value}`,
    commented: false,
    convar: { name, value, scope: "cmd" },
  };
}

function setVar(
  scope: "set" | "setr" | "sets",
  name: string,
  value: string,
  commented = false,
): Directive {
  return {
    key: `var:${name.toLowerCase()}`,
    line: `${commented ? "# " : ""}${scope} ${name} ${value}`,
    commented,
    ...(commented ? {} : { convar: { name, value, scope } }),
  };
}

/**
 * How a directive is written into a line that already exists.
 *
 * The value is this module's business; the spelling is the user's. A file that
 * writes `set sv_hostname` keeps `set sv_hostname`, because changing a keyword
 * the user chose is a change to their file that has nothing to do with what they
 * asked for.
 *
 * The one exception is a keyword that is not a spelling. `setr` and `sets` are
 * what make a value leave the server — `sets sv_projectName` is why the server
 * browser has a project name at all — so a directive this module writes as
 * `sets` is written as `sets` even when the file has the convar as a plain
 * command or a plain `set`. That is a real change to what the line does, and it
 * reaches the user the same way every other rewrite does: through the edit list.
 */
function rewriteAs(line: CfgLine, directive: Directive): string {
  const convar = directive.convar;
  if (convar === undefined) return directive.line;
  const keyword = tokenize(splitComment(line.raw).code)[0] ?? "";
  const fileReplicates = SET_KEYWORDS.get(keyword.toLowerCase());
  const wantsReplication = convar.scope === "setr" || convar.scope === "sets";
  if (wantsReplication && fileReplicates !== true) return directive.line;
  return fileReplicates === undefined
    ? `${convar.name} ${convar.value}`
    : `${keyword} ${convar.name} ${convar.value}`;
}

/**
 * Every non-`ensure` directive a configuration asks for, in file order.
 *
 * One function so the generated file and a merge into an existing file cannot
 * write the same setting two different ways. Sections are comment `Directive`s
 * with no key, which is what keeps the generated file readable without giving
 * the merge anything to match against.
 */
function directivesFor(config: CfgConfig): { section: string; directives: Directive[] }[] {
  const sections: { section: string; directives: Directive[] }[] = [];
  const add = (section: string, directives: (Directive | null)[]): void => {
    const kept = directives.filter((d): d is Directive => d !== null);
    if (kept.length > 0) sections.push({ section, directives: kept });
  };

  // Nothing is defaulted here, and that is the rule the merge depends on. A
  // field left undefined produces no directive, so merging a configuration that
  // says nothing about the port cannot rewrite the port somebody's host gave
  // them — silently changing a setting the user never mentioned is the same
  // class of fault as the silent clamp. `generateServerCfg` fills its own
  // defaults in before calling this, because a file written from scratch does
  // need a port and a slot count.
  const port = config.port;
  add("Endpoints", [
    port === undefined ? null : command("endpoint_add_tcp", quoteCfgValue(`0.0.0.0:${port}`)),
    port === undefined ? null : command("endpoint_add_udp", quoteCfgValue(`0.0.0.0:${port}`)),
  ]);

  add("What the server browser shows", [
    config.hostname === undefined
      ? null
      : command("sv_hostname", quoteCfgValue(config.hostname)),
    config.projectName === undefined
      ? null
      : setVar("sets", "sv_projectName", quoteCfgValue(config.projectName)),
    config.projectDesc === undefined
      ? null
      : setVar("sets", "sv_projectDesc", quoteCfgValue(config.projectDesc)),
    config.tags === undefined
      ? null
      : setVar("sets", "tags", quoteCfgValue([...config.tags].join(", "))),
    config.locale === undefined ? null : setVar("sets", "locale", quoteCfgValue(config.locale)),
  ]);

  const oneSync = config.oneSync;
  add("Slots and state awareness", [
    config.maxClients === undefined
      ? null
      : command("sv_maxclients", String(config.maxClients)),
    oneSync === undefined ? null : setVar("set", "onesync", oneSync),
    config.oneSyncPopulation === undefined
      ? null
      : setVar("set", "onesync_population", config.oneSyncPopulation ? "true" : "false"),
  ]);

  const build = config.enforceGameBuild;
  if (build !== undefined && build !== null && String(build).trim() !== "") {
    const value = String(build).trim();
    const caption = gameBuild(value) ? `  # ${gameBuildLabel(value)}` : "";
    add("Game build", [
      {
        key: "var:sv_enforcegamebuild",
        line: `sv_enforceGameBuild ${value}${caption}`,
        commented: false,
        // The caption is a comment, and it belongs to a line this module wrote.
        // Rewriting somebody else's line writes the number and keeps whatever
        // comment they already had on it, rather than stacking two.
        convar: { name: "sv_enforceGameBuild", value, scope: "cmd" },
      },
    ]);
  }

  add("Keys — treat this file as a secret once these are filled in", [
    config.licenseKey === undefined
      ? null
      : config.licenseKey.trim() === ""
        ? {
            key: "var:sv_licensekey",
            line: '# sv_licenseKey "your key from keymaster.fivem.net"',
            commented: true,
          }
        : command("sv_licenseKey", quoteCfgValue(config.licenseKey)),
    config.steamWebApiKey === undefined || config.steamWebApiKey.trim() === ""
      ? setVar("set", "steam_webApiKey", '"none"', true)
      : setVar("set", "steam_webApiKey", quoteCfgValue(config.steamWebApiKey)),
    config.rconPassword === undefined || config.rconPassword.trim() === ""
      ? { key: "var:rcon_password", line: '# rcon_password ""', commented: true }
      : command("rcon_password", quoteCfgValue(config.rconPassword)),
  ]);

  add("Hardening", [
    config.scriptHookAllowed === undefined
      ? null
      : command("sv_scriptHookAllowed", config.scriptHookAllowed ? "1" : "0"),
    config.endpointPrivacy === undefined
      ? null
      : command("sv_endpointPrivacy", config.endpointPrivacy ? "true" : "false"),
  ]);

  add("Database", [
    config.mysqlConnectionString === undefined || config.mysqlConnectionString.trim() === ""
      ? setVar(
          "set",
          "mysql_connection_string",
          '"mysql://user:password@localhost/database?charset=utf8mb4"',
          true,
        )
      : setVar("set", "mysql_connection_string", quoteCfgValue(config.mysqlConnectionString)),
  ]);

  if (Array.isArray(config.extraSets) && config.extraSets.length > 0) {
    add(
      "Your own settings",
      config.extraSets.map((entry) =>
        entry && typeof entry === "object" && typeof entry.name === "string"
          ? setVar(entry.scope ?? "set", entry.name, quoteCfgValue(String(entry.value ?? "")))
          : null,
      ),
    );
  }

  return sections;
}

/** `add_ace` / `add_principal` lines. Not `Directive`s: they repeat by design. */
function adminLines(config: CfgConfig): string[] {
  const admins = Array.isArray(config.admins) ? config.admins : [];
  if (admins.length === 0) return [];
  const lines = [
    "add_ace group.admin command allow",
    "add_ace group.admin command.quit deny",
    "add_ace group.admin command.start deny",
  ];
  for (const identifier of admins) {
    lines.push(`add_principal identifier.${identifier} group.admin`);
  }
  return lines;
}

// ─── Generating ──────────────────────────────────────────────────────────────

/**
 * The ensure lines, and the comment that says how honest their order is.
 *
 * Categories are never written. `ensure [cars]` starts a folder as a unit in
 * whatever order the server finds it in, which throws away the order this whole
 * module computed; the members are written individually instead, and a comment
 * says the category they came from so the reader can still see the grouping.
 */
function ensureLines(ordered: OrderedCfgResources): string[] {
  const lines: string[] = [];
  if (ordered.source === "dependency-graph") {
    lines.push("# Each resource starts after everything it depends on.");
  } else {
    lines.push("# NOT ORDERED — these are in the order they were given. Check them by hand.");
  }
  for (const note of ordered.notes) lines.push(`# ${commentSafe(note)}`);

  let written = 0;
  let lastCategory: string | undefined;
  for (const resource of ordered.resources) {
    if (written >= MAX_ENSURE_LINES) {
      lines.push(
        `# ${ordered.resources.length - written} more resources are not listed: this file stops ` +
          `at ${MAX_ENSURE_LINES} ensure lines.`,
      );
      break;
    }
    if (resource.category !== undefined && resource.category !== lastCategory) {
      lines.push(`# from ${commentSafe(resource.category)}`);
    }
    lastCategory = resource.category;
    lines.push(`ensure ${cfgResourceToken(resource.name)}`);
    written += 1;
  }
  return lines;
}

/**
 * How a resource name is written on an `ensure` line.
 *
 * A name with a space in it is quoted, which is the only way any config can name
 * it — and it still will not work, because FiveM's own resource scanner will not
 * load a folder with a space in its name. `cfg.name.space` is the finding that
 * says so. Quoting it here means the generated file names the folder the user
 * actually has rather than starting a phantom resource called by its first word.
 */
function cfgResourceToken(name: string): string {
  return /^[A-Za-z0-9_\-.[\]]+$/.test(name) ? name : quoteCfgValue(name);
}

/**
 * Text that is safe to put after a `#`, on one line.
 *
 * A comment is only a comment until the next line break. The adjustment notes
 * at the top of a generated file quote the value the user *gave* — deliberately,
 * because "you gave X, this file uses Y" is the whole point of reporting a
 * clamp — and the value they gave is the unsanitised one, which is exactly where
 * a line break lives. Interpolated raw, a hostname with a newline in it ends the
 * comment and leaves the rest of the hostname sitting on its own line, where
 * this module's own parser reads it as a command and the server tries to run it.
 *
 * That is the competitor's defect ("a newline splits one directive into two
 * lines") reproduced inside the file this tool writes, so the flattening happens
 * at every point where an arbitrary string is interpolated into a comment rather
 * than being left to the caller to remember. Everything unwritable goes the same
 * way it goes in a value — replaced with a space — because the sentence has to
 * stay readable and the character cannot survive either way.
 */
function commentSafe(text: string): string {
  return String(text).replace(CONTROL_CHARACTERS, " ").replace(/ {2,}/g, " ").trim();
}

/**
 * Write a whole `server.cfg`.
 *
 * The graph is optional and its absence is visible in the output: with no graph
 * the ensure block is headed `NOT ORDERED`, because a file that looks ordered
 * and is not is worse than one that admits it.
 *
 * Every line of what comes back is a comment, a blank or a directive this
 * module's own parser reads back as the directive it wrote — including the
 * comment block that quotes what the user typed. `serverCfg.test.ts` asserts
 * exactly that over the hostile hostname, because "valid output in every case"
 * is a claim about the whole file and not about one line of it.
 */
export function generateServerCfg(config: CfgConfig, graph?: DependencyGraph): string {
  const { config: base, adjustments } = normalizeCfgConfig(config);
  // A file written from scratch needs a port and a slot count whether or not the
  // form filled them in; a merge into an existing file must not invent either.
  // See `directivesFor`: the defaults live here and nowhere else.
  const normalized: CfgConfig = {
    ...base,
    port: base.port ?? DEFAULT_PORT,
    maxClients: base.maxClients ?? DEFAULT_MAX_CLIENTS,
  };
  const lines: string[] = [];

  lines.push("# server.cfg — generated by The Workshop CFG Builder.");
  lines.push("# Built in your browser. Nothing about this server was uploaded anywhere.");
  lines.push(`# Game builds: ${GAME_BUILDS_STAMP}.`);

  if (adjustments.length > 0) {
    lines.push("");
    lines.push("# ── Changed while writing this file ──");
    for (const adjustment of adjustments) {
      lines.push(
        `# ${commentSafe(adjustment.field)}: you gave ${commentSafe(adjustment.given)}, this file ` +
          `uses ${commentSafe(adjustment.used)}.`,
      );
      lines.push(`#   ${commentSafe(adjustment.reason)}`);
    }
  }

  for (const { section, directives } of directivesFor(normalized)) {
    lines.push("");
    lines.push(`# ── ${section} ──`);
    for (const directive of directives) lines.push(directive.line);
  }

  const given = Array.isArray(normalized.resources) ? normalized.resources : [];
  const provided = normalized.startServerProvided === false ? [] : CFX_DEFAULT_START_ORDER;
  if (provided.length > 0) {
    lines.push("");
    lines.push("# ── Resources the server itself ships ──");
    lines.push("# These come with the artifacts, not with your pack, and start first.");
    for (const name of provided) lines.push(`ensure ${name}`);
  }

  // The block above has already started these, and the ordinary shape of a
  // FiveM `resources/` folder holds copies of all six inside `[cfx-default]`.
  // Written twice, the file carries exactly the fault `cfg.ensure.duplicate`
  // exists to report — and the duplicated names are the ones whose position
  // matters, because `mapmanager` and `spawnmanager` have to come before
  // `basic-gamemode`. So the folder's copies are subtracted here rather than
  // left for the reader to notice, and a comment says where they went.
  const providedNames = new Set(provided.map((name) => normalizeName(name)));
  const resources =
    providedNames.size === 0
      ? given
      : given.filter((resource) => !providedNames.has(normalizeName(resource.name)));
  const alsoInFolder = given.length - resources.length;

  lines.push("");
  lines.push("# ── Your resources ──");
  if (alsoInFolder > 0) {
    lines.push(
      `# ${alsoInFolder} of the folder's resources ${alsoInFolder === 1 ? "is one the" : "are ones the"} ` +
        "server ships and are started in the block above, so they are not repeated here.",
    );
  }
  if (resources.length === 0) {
    lines.push(
      given.length === 0
        ? "# No resources/ folder was loaded, so nothing is started here."
        : "# Everything in the folder that was loaded is started in the block above.",
    );
    if (given.length === 0) {
      lines.push("# Drop your resources folder into the tool to have this block written for you.");
    }
  } else {
    for (const line of ensureLines(orderCfgResources(resources, graph))) lines.push(line);
  }

  const execs = Array.isArray(normalized.execs)
    ? normalized.execs.filter((path): path is string => typeof path === "string" && path !== "")
    : [];
  if (execs.length > 0) {
    lines.push("");
    lines.push("# ── Other config files ──");
    for (const path of execs) lines.push(`exec ${quoteCfgValue(path)}`);
  }

  const admins = adminLines(normalized);
  if (admins.length > 0) {
    lines.push("");
    lines.push("# ── Admins ──");
    for (const line of admins) lines.push(line);
  }

  return `${lines.join("\n")}\n`;
}

/**
 * The resources the Cfx artifacts ship, in the order their own default
 * `server.cfg` starts them.
 *
 * The *names* are not written here — every one of them is checked against
 * `data/serverProvidedResources.v1.ts` by a test, so there is one list of what
 * the server provides and this file only claims to know the order. If somebody
 * adds a name here that the table does not know, that test fails, which is the
 * point: a typo would otherwise generate `ensure sessionmanger` and a server
 * that never sessions.
 */
export const CFX_DEFAULT_START_ORDER: readonly string[] = [
  "mapmanager",
  "chat",
  "spawnmanager",
  "sessionmanager",
  "basic-gamemode",
  "hardcap",
];

// ─── Merging into an existing config ─────────────────────────────────────────

/**
 * The edits that bring an existing `server.cfg` in line with a configuration,
 * without touching anything the configuration has no opinion about.
 *
 * What is never touched: comments, blank lines, the `exec` lines already in the
 * file, and every command and convar the configuration does not mention. That is
 * the whole value of a merge over a regenerate — a config that has been
 * maintained for two years has notes in it worth more than anything this tool
 * writes — and it is what the byte-for-byte round-trip test in
 * `serverCfg.test.ts` pins down.
 *
 * What is changed: a directive the configuration specifies and the file already
 * has is rewritten in place, in that line's own keyword (`rewriteAs`) and
 * keeping any trailing comment on it; a directive the file does not have is
 * appended; an `exec` path or an admin identifier the configuration carries and
 * the file does not is appended; `ensure` lines are refilled in place, in order,
 * and the surplus left by a de-duplication is commented out rather than deleted.
 *
 * A field the configuration leaves `undefined` produces nothing at all. That is
 * `directivesFor`'s rule and this function depends on it: a caller that fills
 * every field with a default before merging will rewrite settings the user never
 * mentioned, and nothing here can tell that apart from a deliberate change.
 *
 * TWO THINGS THIS CANNOT DO, and a caller that needs them has to do them.
 *
 * There is no graph parameter, so the order written is the order of
 * `next.resources`. A caller holding a graph should pass
 * `orderCfgResources(resources, graph).resources` — the same call
 * `generateServerCfg` makes — rather than hoping this reorders for them.
 *
 * A comment sitting inside the ensure block is left exactly where it is, which
 * means a `# vehicles` heading can end up above a resource that is no longer a
 * vehicle. Moving it would mean guessing what it describes; leaving it is at
 * least visible in the diff the user reviews before saving.
 */
export function mergeServerCfg(existing: ParsedCfg, next: CfgConfig): GtaTextEdit[] {
  const cfg =
    existing && Array.isArray(existing.lines)
      ? existing
      : { lines: [], order: [], categories: new Set<string>(), truncated: [] };
  const { config } = normalizeCfgConfig(next);
  const file = fileOf(cfg);
  const edits: GtaTextEdit[] = [];
  const appended: string[] = [];

  // ── Directives ──
  const lastByKey = new Map<string, CfgLine>();
  for (const line of cfg.lines) {
    const key = directiveKey(line);
    if (key !== null) lastByKey.set(key, line);
  }

  for (const { section, directives } of directivesFor(config)) {
    const missing: string[] = [];
    for (const directive of directives) {
      const line = lastByKey.get(directive.key);
      if (line === undefined) {
        // A commented placeholder is help for an empty file, clutter in a
        // maintained one. Only real directives are appended.
        if (!directive.commented) missing.push(directive.line);
        continue;
      }
      if (directive.commented) continue;
      const suffix = splitComment(line.raw).comment;
      const written = rewriteAs(line, directive);
      const replacement = suffix === "" ? written : `${written}   ${suffix}`;
      if (replacement === line.raw) continue;
      edits.push({
        file,
        start: line.span.byteStart ?? 0,
        end: line.span.byteEnd ?? 0,
        expect: line.raw,
        replacement,
      });
    }
    if (missing.length > 0) {
      appended.push("", `# ── ${section} ──`, ...missing);
    }
  }

  // ── Ensure lines ──
  const ensureEdits = mergeEnsureLines(cfg, config, file);
  edits.push(...ensureEdits.edits);
  if (ensureEdits.appended.length > 0) {
    appended.push("", "# ── Resources ──", ...ensureEdits.appended);
  }

  // ── `exec` lines and admins ──
  //
  // Both used to be written by the generator alone, which meant a user who typed
  // three admin identifiers and clicked the vMenu preset, then pasted their own
  // config to patch it, got a file with no admins and no permissions exec and a
  // line on screen saying everything else was byte for byte what they gave us.
  // A silent drop is the one option this layer rules out, so they are appended
  // here the same way a missing directive is. Nothing existing is rewritten: an
  // `exec` line points at a file this module cannot read, and an `add_principal`
  // line already in the config is somebody's access, not a value to update.
  const execsWanted = Array.isArray(config.execs)
    ? config.execs.filter((path): path is string => typeof path === "string" && path.trim() !== "")
    : [];
  if (execsWanted.length > 0) {
    const have = new Set(
      cfg.lines
        .filter((line): line is Extract<CfgLine, { kind: "exec" }> => line.kind === "exec")
        .map((line) => execKey(line.path)),
    );
    const missingExecs = execsWanted
      .filter((path) => !have.has(execKey(path)))
      .map((path) => `exec ${quoteCfgValue(path)}`);
    if (missingExecs.length > 0) appended.push("", "# ── Other config files ──", ...missingExecs);
  }

  const admins = adminLines(config);
  if (admins.length > 0) {
    const have = new Set(
      cfg.lines
        .filter((line) => line.kind === "command")
        .map((line) => tokenize(splitComment(line.raw).code).join(" ").toLowerCase()),
    );
    const missingAdmins = admins.filter((line) => !have.has(line.toLowerCase()));
    if (missingAdmins.length > 0) appended.push("", "# ── Admins ──", ...missingAdmins);
  }

  // ── One insertion at the end, because two insertions at the same offset
  //    overlap and `applyEdits` refuses both. ──
  if (appended.length > 0) {
    const end = endOffset(cfg);
    const needsBreak = !endsWithNewline(cfg);
    edits.push({
      file,
      start: end,
      end,
      expect: "",
      replacement: `${needsBreak ? "\n" : ""}${appended.join("\n")}\n`,
    });
  }

  return edits;
}

/**
 * Two spellings of the same `exec` target, as one key.
 *
 * A path is a path whichever slash it was typed with and whatever case the
 * filesystem folds; comparing the raw strings would append a second
 * `exec vMenu/config/permissions.cfg` under the user's
 * `exec vmenu\config\permissions.cfg` and run their permissions file twice.
 */
function execKey(path: string): string {
  return path.trim().replace(/\\/g, "/").replace(/^\.\//, "").toLowerCase();
}

function fileOf(cfg: ParsedCfg): string {
  for (const line of cfg.lines) {
    if (typeof line.span.file === "string" && line.span.file !== "") return line.span.file;
  }
  return "server.cfg";
}

function endOffset(cfg: ParsedCfg): number {
  let end = 0;
  for (const line of cfg.lines) end = Math.max(end, line.span.byteEnd ?? 0);
  return end;
}

/** True when the last parsed line is the empty one a trailing newline leaves. */
function endsWithNewline(cfg: ParsedCfg): boolean {
  const last = cfg.lines[cfg.lines.length - 1];
  return last !== undefined && last.kind === "blank" && last.raw === "";
}

/**
 * Refill the existing `ensure` slots in the right order and append the rest.
 *
 * Slot-filling rather than delete-and-rewrite because deleting a line somebody
 * wrote is the one thing a merge must not do casually. The desired list is the
 * configuration's resources followed by anything the file already started that
 * the configuration does not know about — a resource installed by hand, a
 * library living outside the folder that was dropped — so the desired list is
 * never shorter than the file's unless the file repeated itself, and a repeat is
 * commented out rather than removed.
 */
function mergeEnsureLines(
  cfg: ParsedCfg,
  config: CfgConfig,
  file: string,
): { edits: GtaTextEdit[]; appended: string[] } {
  const slots = cfg.lines.filter(
    (line): line is Extract<CfgLine, { kind: "ensure" }> => line.kind === "ensure",
  );
  const resources = Array.isArray(config.resources) ? config.resources : [];
  if (resources.length === 0) return { edits: [], appended: [] };

  const desired: string[] = [];
  const claimed = new Set<string>();
  for (const resource of resources) {
    const id = normalizeName(resource.name);
    if (id === "" || claimed.has(id)) continue;
    claimed.add(id);
    desired.push(cfgResourceToken(resource.name));
  }
  for (const line of slots) {
    const id = normalizeName(line.resource);
    if (id === "" || claimed.has(id)) continue;
    claimed.add(id);
    desired.push(line.resource);
  }

  const edits: GtaTextEdit[] = [];
  slots.forEach((line, index) => {
    const wanted = desired[index];
    const suffix = splitComment(line.raw).comment;
    const replacement =
      wanted === undefined
        ? `# ${line.raw.trim()}   # already started above; this line did nothing`
        : suffix === ""
          ? `ensure ${wanted}`
          : `ensure ${wanted}   ${suffix}`;
    if (replacement === line.raw) return;
    edits.push({
      file,
      start: line.span.byteStart ?? 0,
      end: line.span.byteEnd ?? 0,
      expect: line.raw,
      replacement,
    });
  });

  const appended = desired.slice(slots.length).map((name) => `ensure ${name}`);
  return { edits, appended };
}

// ─── Findings ────────────────────────────────────────────────────────────────

export type CfgAnalysisInput = {
  /** The config as it stands, when there is one to read. */
  cfg?: ParsedCfg | null;
  /** The form state or the merge target, when there is one. */
  config?: CfgConfig | null;
  /**
   * The folder's resources. `undefined` means no folder was loaded, which is a
   * different answer from an empty array (a folder with nothing in it) and
   * produces a `skipped` line rather than a clean bill of health.
   */
  resources?: readonly CfgResource[];
  /** The bracket categories present in the folder, `[cars]`, lower-cased. */
  categories?: readonly string[];
  graph?: DependencyGraph | null;
};

export type CfgAnalysis = {
  findings: GtaFinding[];
  /** Checks that did not run, and why. Empty when everything ran. */
  skipped: string[];
};

/** Placeholder licence keys, the ones people leave in. */
const LICENCE_PLACEHOLDERS: readonly RegExp[] = [
  /^changeme$/i,
  /^your[_\s-]?licen[cs]e[_\s-]?key/i,
  /licen[cs]e[_\s-]?key[_\s-]?here/i,
  /^cfxk_x+/i,
  /^x{4,}$/i,
  /^<.*>$/,
  /^replace[_\s-]?me$/i,
];

function isPlaceholderLicence(value: string): boolean {
  const trimmed = value.trim();
  if (trimmed === "") return true;
  return LICENCE_PLACEHOLDERS.some((pattern) => pattern.test(trimmed));
}

/** Families that do not run without a database behind them. */
const DATABASE_FAMILIES: ReadonlySet<string> = new Set(["esx", "qbcore", "qbox", "rsg"]);

/** Named connectors: present at all means a connection string is expected. */
const DATABASE_CONNECTORS: ReadonlySet<string> = new Set([
  "oxmysql",
  "mysql-async",
  "ghmattimysql",
]);

function spanOf(line: CfgLine | undefined): GtaSourceSpan {
  return line ? { ...line.span } : {};
}

/**
 * What is worth telling somebody about their `server.cfg`.
 *
 * Every check here says what it did not check. `resources` left undefined means
 * no folder was loaded, and the four checks that compare the config against a
 * folder come back as `skipped` lines instead of silence — because silence from
 * a check that never ran reads exactly like a pass, which is the failure this
 * whole layer is built around.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHEN A FILE AND A CONFIGURATION BOTH SAY SOMETHING, THE FILE WINS
 *
 * This report is about the config in front of the user. A `cfg` was read from
 * their disk; a `config` is a form somebody has not saved yet, and the surface
 * that holds that form fills every field it owns, defaults included. Letting the
 * form win meant a pasted config with `sv_maxclients 64` and `set onesync off`
 * — the pair the import fixture exists for — reported nothing at all, because
 * the form said 48 and `on`; and a pasted config with a real licence key and a
 * real connection string was told its key was a placeholder and its database
 * missing, because the form's two boxes were empty strings and `??` does not
 * fall through on `""`. Both are the honesty rule inverted: a confident report
 * about values the user never typed, over a file that was right.
 *
 * So every setting below is read from the file first and from the configuration
 * only when the file is silent. With no `cfg` — form mode — the configuration is
 * all there is and nothing changes. The one deliberate consequence: with a
 * config imported, the panel describes the file as it stands rather than as it
 * will stand after the merge. That is the honest half of the pair, because the
 * merge is the thing the user has not agreed to yet.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export function analyzeServerCfg(input: CfgAnalysisInput): CfgAnalysis {
  const source = input !== null && typeof input === "object" ? input : {};
  const cfg = source.cfg ?? null;
  const config = source.config ?? null;
  const graph = source.graph ?? null;
  const folderLoaded = Array.isArray(source.resources);
  const resources = folderLoaded ? (source.resources as readonly CfgResource[]) : [];
  const findings: GtaFinding[] = [];
  const skipped: string[] = [];

  // The cap reports itself, like every other cap in this module. A panel that
  // stops at 500 rows and says nothing is a report that reads as complete over a
  // folder where a hundred resources were never mentioned.
  let dropped = 0;
  const push = (finding: GtaFinding): void => {
    if (findings.length < MAX_CFG_FINDINGS) findings.push(finding);
    else dropped += 1;
  };

  // The start order this analysis reasons about: what the file says, or what a
  // generated file would say.
  const ensureLinesInFile = (cfg?.lines ?? []).filter(
    (line): line is Extract<CfgLine, { kind: "ensure" }> => line.kind === "ensure",
  );
  const started: { name: string; line?: CfgLine }[] = cfg
    ? ensureLinesInFile.map((line) => ({ name: line.resource, line }))
    : (config?.resources ?? []).map((resource) => ({ name: resource.name }));

  const firstIndexById = new Map<string, number>();
  started.forEach((entry, index) => {
    const id = normalizeName(entry.name);
    if (id !== "" && !firstIndexById.has(id)) firstIndexById.set(id, index);
  });

  const folderById = new Map<string, CfgResource>();
  for (const resource of resources) {
    const id = normalizeName(resource.name);
    if (id !== "") folderById.set(id, resource);
  }
  const folderCategories = new Set(
    (Array.isArray(source.categories) ? source.categories : [])
      .map((name) => String(name).toLowerCase())
      .concat(
        resources
          .map((resource) => resource.category?.toLowerCase())
          .filter((name): name is string => typeof name === "string"),
      ),
  );

  // ── cfg.ensure.duplicate ──
  const seen = new Map<string, number>();
  started.forEach((entry) => {
    const id = normalizeName(entry.name);
    if (id === "") return;
    const count = (seen.get(id) ?? 0) + 1;
    seen.set(id, count);
    if (count !== 2) return;
    push({
      ...spanOf(entry.line),
      code: "cfg.ensure.duplicate",
      severity: "warning",
      title: `${entry.name} is started more than once`,
      detail:
        `This config has ${entry.name} on more than one start line. The server starts it once and ` +
        "ignores the rest, so the second line does nothing — but it also hides the resource's real " +
        "position in the order, and somebody editing the first line later will not see the second. " +
        "Delete the extra line.",
      entity: { kind: "resource", id },
    });
  });

  // ── cfg.name.space ──
  //
  // Computed before everything below it, because one folder with a space in its
  // name is three findings if the checks are left to run independently: the
  // folder is named wrong, the line starts a resource that does not exist, and
  // the folder is never started. They are one fault with one fix, and a reader
  // who has to work that out for themselves has been handed noise. The line
  // finding is the specific one — it can quote both spellings — so it wins, and
  // the sets below suppress the other two for the same folder.
  const spaceById = new Set<string>();
  const spaceLineIndex = new Set<number>();
  ensureLinesInFile.forEach((line, index) => {
    const tokens = tokenize(splitComment(line.raw).code);
    if (tokens.length <= 2) return;
    const joined = tokens.slice(1).join(" ");
    const id = normalizeName(joined);
    if (!folderById.has(id)) return;
    spaceLineIndex.add(index);
    spaceById.add(id);
    push({
      ...spanOf(line),
      code: "cfg.name.space",
      severity: "error",
      title: `This line starts "${tokens[1]}", not ${joined}`,
      detail:
        `The folder is called ${joined}, and the space in its name makes this line name only its ` +
        `first word. The server looks for a resource called "${tokens[1]}", does not find one, and ` +
        `${joined} never starts — no line of the log says so, because as far as the server is ` +
        `concerned you asked for "${tokens[1]}". Rename the folder to ` +
        `${joined.replace(/\s+/g, "_")} and change this line to match.`,
      entity: { kind: "resource", id },
    });
  });
  for (const resource of resources) {
    const id = normalizeName(resource.name);
    if (!resource.name.includes(" ") || spaceById.has(id)) continue;
    spaceById.add(id);
    push({
      code: "cfg.name.space",
      severity: "error",
      title: `The folder ${resource.name} has a space in its name`,
      detail:
        `FiveM splits a config line on spaces, so \`ensure ${resource.name}\` starts a resource ` +
        `called "${resource.name.split(" ")[0]}" and silently ignores the rest — and the server's ` +
        "own resource scanner will not load a folder with a space in its name in the first place. " +
        `Rename the folder to ${resource.name.replace(/\s+/g, "_")} and update anything that ` +
        "references it.",
      entity: { kind: "resource", id },
    });
  }

  // ── The folder comparison, or the reason it did not happen ──
  if (!folderLoaded) {
    skipped.push(
      "No resources folder was loaded, so nothing here was checked against what is actually " +
        "installed: a resource started but not present, a resource present but never started, and " +
        "a bracket category that does not exist all go unreported.",
    );
  } else {
    // ── cfg.ensure.missing ──
    started.forEach((entry, index) => {
      const id = normalizeName(entry.name);
      if (id === "" || folderById.has(id)) return;
      if (isCategoryName(entry.name)) return; // handled by cfg.ensure.category
      if (isServerProvidedResource(id)) return; // ships with the artifacts
      if (spaceLineIndex.has(index)) return; // already reported as cfg.name.space
      push({
        ...spanOf(entry.line),
        code: "cfg.ensure.missing",
        severity: "warning",
        title: `${entry.name} is started but is not in the folder`,
        detail:
          `Nothing in the folder that was loaded is called ${entry.name}. If it lives somewhere ` +
          "else on the server this line is fine and there is nothing to do; if it does not, the " +
          "server logs one failed start at boot and every resource that depends on it fails after " +
          "it. Check the spelling against the folder name, or remove the line.",
        entity: { kind: "resource", id },
      });
    });

    // ── cfg.ensure.unstarted ──
    //
    // Once per name, not once per folder. Two vendor packs that both ship
    // `police/` are two folders and one resource name: a `server.cfg` can only
    // ever name one of them, so a second row reading "police is in the folder
    // but never started" — with the same title, the same detail and the same
    // entity id as the first — tells the reader nothing except that something is
    // repeating itself. That two packs collide is a real and separate fact, and
    // it is reported where it can actually be pointed at: the surface inspects
    // the drop by root, before names collapse, and names every colliding path.
    const unstartedSeen = new Set<string>();
    for (const resource of resources) {
      const id = normalizeName(resource.name);
      if (id === "" || firstIndexById.has(id) || spaceById.has(id)) continue;
      if (unstartedSeen.has(id)) continue;
      unstartedSeen.add(id);
      const category = resource.category?.toLowerCase();
      if (category !== undefined && cfg?.categories.has(category)) continue;
      push({
        code: "cfg.ensure.unstarted",
        severity: "info",
        title: `${resource.name} is in the folder but never started`,
        detail:
          `${resource.name} is sitting in the resources folder with no start line anywhere in this ` +
          "config, so the server never loads it. That is fine for something kept on the shelf " +
          "deliberately; it is the usual reason a newly installed resource does nothing. Add " +
          `\`ensure ${cfgResourceToken(resource.name)}\` if it is meant to run.`,
        entity: { kind: "resource", id },
      });
    }

    // ── cfg.ensure.category ──
    for (const entry of started) {
      if (!isCategoryName(entry.name)) continue;
      const name = entry.name.toLowerCase();
      if (folderCategories.has(name)) continue;
      push({
        ...spanOf(entry.line),
        code: "cfg.ensure.category",
        severity: "warning",
        title: `${entry.name} is not a category folder in this folder`,
        detail:
          `\`ensure ${entry.name}\` starts every resource inside a folder literally named ` +
          `${entry.name}, brackets and all. There is no such folder here, so this line does ` +
          "nothing at all — it does not even log an error, which is why a config can carry one " +
          "for years. Start the resources individually, or check the folder's real name.",
        entity: { kind: "resource", id: normalizeName(entry.name) },
      });
    }
    for (const resource of resources) {
      const category = resource.category?.toLowerCase();
      if (category === undefined || !cfg?.categories.has(category)) continue;
      const id = normalizeName(resource.name);
      if (!firstIndexById.has(id)) continue;
      push({
        ...spanOf(ensureLinesInFile[firstIndexById.get(id) ?? 0]),
        code: "cfg.ensure.category",
        severity: "warning",
        title: `${resource.name} is started twice: on its own and as part of ${resource.category}`,
        detail:
          `\`ensure ${resource.category}\` already starts everything in that folder, including ` +
          `${resource.name}. Starting it again on its own line changes nothing the server does, ` +
          "but it means the order you can see here is not the order the server uses — the category " +
          "line decides that, and it starts its members in folder order. Pick one: either drop the " +
          "category line and start every member individually in the right order, or drop this line.",
        entity: { kind: "resource", id },
      });
    }
  }

  // ── cfg.ensure.order and cfg.ensure.cycle ──
  if (!graph) {
    skipped.push(
      "No dependency graph was supplied, so start order was not checked: a resource started " +
        "before something it needs, a dependency cycle, and a dependency that is not installed " +
        "at all go unreported.",
    );
  } else {
    const nodeLabel = new Map(
      (graph.nodes ?? []).map((node) => [node.id, node.label] as const),
    );
    const localIds = new Set(
      (graph.nodes ?? []).filter((node) => node.status === "local").map((node) => node.id),
    );

    for (const edge of graph.edges ?? []) {
      if (!edge || !localIds.has(edge.to)) continue;
      const fromAt = firstIndexById.get(edge.from);
      const toAt = firstIndexById.get(edge.to);
      if (fromAt === undefined || toAt === undefined || toAt < fromAt) continue;
      const fromName = nodeLabel.get(edge.from) ?? edge.from;
      const toName = nodeLabel.get(edge.to) ?? edge.to;
      const inferred = edge.kind === "inferred";
      push({
        ...spanOf(ensureLinesInFile[fromAt]),
        code: "cfg.ensure.order",
        // The code's usual severity is `error`. An inferred edge is a guess read
        // out of script text, and this layer does not raise an error on a guess
        // — `types.ts` is explicit that severity belongs to the occurrence and
        // not to the code, and this is the occurrence it had in mind.
        severity: (inferred ? "warning" : "error") as GtaSeverity,
        title: `${fromName} starts before ${toName}, which it needs`,
        detail:
          `${fromName} is started ${toAt === fromAt ? "at the same point as" : "before"} ` +
          `${toName}, and it depends on it. At boot the export or event it reaches for is not ` +
          "registered yet, so the feature is dead until somebody restarts the resource by hand — " +
          "which is exactly why this kind of fault looks intermittent and survives a week of " +
          `testing. Move \`ensure ${toName}\` above \`ensure ${fromName}\`.` +
          (inferred
            ? " This dependency was inferred from script text rather than declared in a manifest, " +
              "so confirm it before reordering."
            : ""),
        entity: { kind: "resource", id: edge.from },
        ...(inferred ? { tags: ["inferred" as const] } : {}),
      });
    }

    for (const cycle of graph.cycles ?? []) {
      if (!Array.isArray(cycle) || cycle.length === 0) continue;
      const labels = cycle.map((id) => nodeLabel.get(id) ?? id);
      const loop = [...labels, labels[0]].join(" → ");
      push({
        code: "cfg.ensure.cycle",
        severity: "error",
        title:
          cycle.length === 1
            ? `${labels[0]} depends on itself`
            : `${labels.join(" and ")} depend on each other`,
        detail:
          `${loop}. There is no start order that puts all of these after everything they need, so ` +
          "whichever one starts first comes up with a dependency that is not running. This config " +
          "still starts every one of them — dropping a resource because it sits in a loop would " +
          "hide the problem rather than solve it — but the order inside the loop is a guess. Break " +
          "the loop by moving whatever they share into a resource of its own.",
        entity: { kind: "resource", id: cycle[0] },
      });
    }

    // ── Dependencies that are not in the folder at all ──
    //
    // The brief's sentence for folder mode is "cycles and missing dependencies
    // reported rather than silently dropped", and only the first half was here:
    // the `cfg.ensure.order` loop above skips every edge pointing at a node that
    // is not local, so a folder whose resource declares an uninstalled
    // dependency produced no finding, no skipped line, and an ensure block
    // headed "Each resource starts after everything it depends on" over a
    // resource that cannot start.
    //
    // `dependencies.ts` already owns this check, names the claimants and offers
    // the near-miss spelling, so it is delegated rather than rewritten — one
    // answer per question is the same rule that keeps the ordering there. Only
    // `dependency.missing` is taken: `dependency.cycle` is `cfg.ensure.cycle`
    // above, said in the vocabulary of a config, and `dependency.inferred` is a
    // guess this report already carries as the `inferred` tag on
    // `cfg.ensure.order`. Both would be the same fault twice on one screen.
    for (const finding of dependencyFindings(graph)) {
      if (finding.code === "dependency.missing") push(finding);
    }
  }

  // ── cfg.onesync.maxclients ──
  //
  // File first, configuration second. See the header: with a config imported,
  // the form's untouched defaults are not a claim about somebody else's file.
  const maxClients =
    numberOrNull(readCfgValue(cfg, "sv_maxclients")) ??
    (typeof config?.maxClients === "number" ? config.maxClients : null);
  const oneSync = (readCfgValue(cfg, "onesync") ?? config?.oneSync ?? "")
    .toString()
    .toLowerCase();
  if (maxClients !== null && maxClients > ONESYNC_OFF_CLIENT_CAP) {
    if (oneSync === "off" || oneSync === "false" || oneSync === "0") {
      push({
        code: "cfg.onesync.maxclients",
        severity: "warning",
        title: `${maxClients} slots with OneSync off runs ${ONESYNC_OFF_CLIENT_CAP} slots`,
        detail:
          `OneSync is off in this config and \`sv_maxclients\` is ${maxClients}. Without OneSync ` +
          `the server caps at ${ONESYNC_OFF_CLIENT_CAP} players and says nothing about it, so the ` +
          `slot count you advertise and the slot count you have are different numbers. Set ` +
          "`set onesync on` if you want the slots, or lower the count so the config tells the truth.",
      });
    } else if (oneSync === "") {
      skipped.push(
        `This config does not set \`onesync\`, so whether ${maxClients} slots is achievable was ` +
          `not checked — with OneSync off the server caps at ${ONESYNC_OFF_CLIENT_CAP}.`,
      );
    }
  }

  // ── cfg.license.placeholder ──
  //
  // A key typed into a form counts; a form box left blank does not, because the
  // surface passes `""` deliberately — it is how the generator is told to write
  // the commented placeholder line — and `""` winning over a key that was read
  // out of the user's own file is the accusation this check must never make.
  // With no file at all, `""` is still the answer: a config written from a blank
  // key has no key, and the check should say so.
  const typedLicence =
    typeof config?.licenseKey === "string" && config.licenseKey.trim() !== ""
      ? config.licenseKey
      : null;
  const licence =
    readCfgValue(cfg, "sv_licenseKey") ?? typedLicence ?? (cfg ? null : (config?.licenseKey ?? null));
  const execsElsewhere = (cfg?.lines ?? []).some((line) => line.kind === "exec");
  if (licence === null || licence === undefined) {
    if (cfg && execsElsewhere) {
      // A key set in an exec'd file is a key this module cannot see, and "you
      // have no licence key" is too confident an accusation to make about a
      // config whose other half was not read.
      skipped.push(
        "No `sv_licenseKey` is in this file, but it `exec`s another one, so whether a key is set " +
          "was not checked.",
      );
    } else if (cfg) {
      push({
        code: "cfg.license.placeholder",
        severity: "warning",
        title: "No licence key is set",
        detail:
          "There is no `sv_licenseKey` line in this config. The server refuses to start without " +
          "one. Generate a key at keymaster.fivem.net and add it — and treat this file as a secret " +
          "once you have, because the key is the server's identity.",
      });
    }
  } else if (isPlaceholderLicence(licence)) {
    push({
      code: "cfg.license.placeholder",
      severity: "warning",
      title: "The licence key is still a placeholder",
      detail:
        `\`sv_licenseKey\` is set to "${licence.trim() === "" ? "" : licence.trim()}", which is a ` +
        "placeholder rather than a key. The server will refuse to start. Generate one at " +
        "keymaster.fivem.net, and keep this file out of any repository once it is real.",
    });
  }

  // ── cfg.build.stale ──
  const buildValue =
    readCfgValue(cfg, "sv_enforceGameBuild") ?? normalizeBuild(config?.enforceGameBuild);
  if (buildValue !== null && buildValue !== "") {
    const behind = gameBuildsBehind(buildValue);
    if (behind === null) {
      skipped.push(
        `\`sv_enforceGameBuild ${buildValue}\` is not a build in our table, so how current it is ` +
          "was not checked. It may simply be newer than this tool.",
      );
    } else if (behind >= STALE_BUILDS_BEHIND) {
      const latest = latestGameBuild();
      push({
        code: "cfg.build.stale",
        severity: "info",
        title: `Game build ${buildValue} is ${behind} releases behind`,
        detail:
          `This config pins the server to build ${gameBuildLabel(buildValue)}, which is ${behind} ` +
          `DLC releases behind ${latest.build} (${latest.dlc}). Any resource that streams content ` +
          "added after that build loads nothing in game, with no error to explain it — which is " +
          "the single most confusing way for a new vehicle or map pack to fail. Raise it unless " +
          "something you run needs the older build.",
      });
    }
  }

  // ── cfg.db.missing ──
  //
  // Same rule as the licence key, and the same blank-string trap: the surface
  // sends an empty `mysqlConnectionString` on purpose, and an empty string
  // winning over the `set mysql_connection_string` line three rows above it in
  // the user's own file is a warning about a config that is correct.
  const typedConnection =
    typeof config?.mysqlConnectionString === "string" &&
    config.mysqlConnectionString.trim() !== ""
      ? config.mysqlConnectionString
      : null;
  const connectionString = readCfgValue(cfg, "mysql_connection_string") ?? typedConnection;
  if (!folderLoaded) {
    skipped.push(
      "No resources folder was loaded, so whether a database connection string is needed was not " +
        "checked.",
    );
  } else if (connectionString === null || connectionString.trim() === "") {
    const needsDatabase = resources
      .filter((resource) => {
        const id = normalizeName(resource.name);
        return DATABASE_CONNECTORS.has(id) || DATABASE_FAMILIES.has(frameworkFamily(id));
      })
      .map((resource) => resource.name);
    if (needsDatabase.length > 0) {
      push({
        code: "cfg.db.missing",
        severity: "warning",
        title: "A framework is installed with no database connection string",
        detail:
          `${needsDatabase.slice(0, 4).join(", ")}${needsDatabase.length > 4 ? " and others" : ""} ` +
          "need a database, and this config has no `mysql_connection_string`. Every one of them " +
          "throws at startup and the server comes up with no characters, no jobs and no inventory. " +
          'Add `set mysql_connection_string "mysql://user:password@localhost/database?charset=utf8mb4"` ' +
          "with your own credentials.",
      });
    }
  }

  if (cfg && cfg.lines.some((line) => line.kind === "exec")) {
    skipped.push(
      "This config `exec`s another file. Whatever that file starts is not in the order read here, " +
        "so a resource started there looks unstarted and its position in the order is unknown.",
    );
  }

  for (const note of cfg?.truncated ?? []) skipped.push(note);

  if (dropped > 0) {
    skipped.push(
      `${dropped.toLocaleString()} further finding${dropped === 1 ? " was" : "s were"} not listed: ` +
        `this report stops at ${MAX_CFG_FINDINGS.toLocaleString()}. What is below is a sample of ` +
        "what is wrong with this config rather than all of it.",
    );
  }

  return { findings, skipped };
}

function numberOrNull(value: string | null): number | null {
  if (value === null) return null;
  const parsed = Number(value.trim());
  return Number.isFinite(parsed) ? parsed : null;
}

function normalizeBuild(value: string | number | null | undefined): string | null {
  if (value === null || value === undefined) return null;
  const text = String(value).trim();
  return text === "" ? null : text;
}
